import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, BehaviorSubject, of } from 'rxjs';
import {tap, switchMap, catchError, filter, take, finalize} from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService, ISession } from './auth.service';
import { environment } from '../../environments/environment';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private router: Router, private authService: AuthService ) {

  }

  isRefreshingToken: any = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    return next.handle( this.addTokenToRequest(request, this.authService.getCsrfAccess()))
        .pipe(catchError( (err: any) => {
           if (err instanceof HttpErrorResponse) {
              switch ((<HttpErrorResponse>err).status) {
                case 401: {
                  if (err.error?.error_code === 'password_expired') {
                    this.router.navigate(['/reset-password'], { queryParams: { expired: 1 } });
                    return of();
                  }
                  if (!this.authService.getCsrfRefresh()) {
                    console.log( 'no session to refresh, logging out');
                    this.authService.logout();
                    return of();
                  }
                  return this.handle401Error(request, next);
                }
                case 400:
                  this.authService.logout();
                  return of();
                case 422: {
                  this.authService.logout();
                  return of();
                }
                default: {
                  console.log( 'Caught error in interceptor' + err );
                  const errResult: any = { 'status': false, 'error': err };
                  return throwError(() => errResult);
                }
              }
          } else {
             console.log( 'Caught error in interceptor' + err );
             return throwError(() => err);
          }
         }));
    }


  /** Send the session cookies, and the CSRF value when there is one, on calls to our API only. */
  private addTokenToRequest(request: HttpRequest<any>, csrf: string): HttpRequest<any> {
    if (!request.url.startsWith(environment.apiUrl)) {
      return request;
    }
    return request.clone({ withCredentials: true, setHeaders: csrf ? { 'X-CSRF-TOKEN': csrf } : {} });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {

    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      // Reset here so that the following requests wait until the new CSRF value
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);

      return this.authService.refreshToken()
        .pipe(
           switchMap((user: ISession) => {
              if (user) {
                  this.tokenSubject.next(user.csrf_access);
                  return next.handle(this.addTokenToRequest(request, user.csrf_access));
                }
                this.authService.logout();
                return throwError(() => user);
           }),
           catchError(err => {
             this.authService.logout();
             return throwError(() => err);
           }),
           finalize(() => {
             this.isRefreshingToken = false;
           })
         );
    } else {
      return this.tokenSubject
        .pipe(filter(token => token != null),
          take(1),
          switchMap(token => {
            return next.handle(this.addTokenToRequest(request, token));
          }),
          catchError(err => {
            this.authService.logout();
            return throwError(() => err);
          })
        );
    }
  }

}

